<template>
    <div class="login__form">
        <div class="login__form-logo">
            <IconLogo class-name="icon-fill icon_color_blue" />
        </div>

        <div class="login__form-title">
            Личный кабинет брокера
        </div>

        <form @submit.prevent="verificationSurname">
            <div class="login__form-input indent_top_yellow">
                <div class="input__wrapper">
                    <div class="input__container">
                        <input
                            id="surname-input"
                            ref="surnameInput"
                            v-model.trim="surname"
                            type="text"
                            name="surname-input"
                            placeholder=" "
                            :class="['input', {'is-error': isSurnameError}]"
                            @focus="clearInputsError">
                        <label for="surname-input"
                               class="input__label">
                            Укажите свою фамилию
                        </label>
                        <span class="input__error"
                              v-html="surnameErrorText" />
                    </div>
                </div>
            </div>

            <div class="login__form-submit">
                <STBaseButton
                    text="Войти в личный кабинет"
                    type="submit"
                    :disabled="isSurnameDisabled"
                    modify="button_full_width" />
            </div>
        </form>

        <STBaseButton v-if="!loading && !hasLoaded"
                      icon-before="IconRight"
                      icon-before-modify="icon-stroke icon_color_white icon-reverse"
                      modify="login__form-back button_size_small button_theme_round button_non_text"
                      @click="goToBack(1)" />

        <STLoader :loading="loading"
                  :has-loaded="hasLoaded"
                  :message="loaderMessage"
                  modify="loader_full_size" />
    </div>
</template>

<script>
import IconLogo from '@/components/Icons/Logo';
import {maxLength, minLength, required} from 'vuelidate/lib/validators';
import {mapActions} from 'vuex';
import {redirectAfterAuth} from '@/helpers/authRedirect';
import {errorTimeMessage} from '@/helpers/errorHelper';

export default {
    name: 'Surname',

    components: {
        IconLogo
    },

    validations: {
        surname: {
            required,
            minLength: minLength(2),
            maxLength: maxLength(40)
        }
    },

    data() {
        return {
            surname         : '', // Фамилия
            loaderMessage   : null, // Текст при запуске лоадера
            loading         : false, // Запущен ли лоадер
            hasLoaded       : false, // Завершилась ли загрузка успехом
            isSurnameError  : false, // Есть ли ошибка с фамилией
            surnameErrorText: 'Фамилия неверная или не&nbsp;найдена' // Текст при ошибке фамилии
        };
    },

    computed: {
        isSurnameDisabled() {
            return this.$v.surname.$invalid;
        }
    },

    mounted() {
        this.$nextTick(() => {
            this.$refs.surnameInput?.focus();
        });
    },

    methods: {
        ...mapActions(['surnameVerification']),

        /**
         * Проверка фамилии
         */
        async verificationSurname() {
            try {
                if (this.$v.surname.$invalid) {
                    return;
                }
    
                this.launchLoader(true, 'Проверка фамилии');
    
                await this.surnameVerification({
                    surName: this.surname
                })
                    .then((response) => {
                        const {data} = response;
    
                        if (data?.error) {
                            this.returnError({
                                code: data?.notCounter ? 1 : 3,
                            });

                            this.launchLoader(false);
                            return;
                        }
    
                        // messageCode
                        // 0 - Ошибка интеграции
                        // 1 - Фамилия совпадает
                        // 2 - Фамилия неверная или не найдена
                        // 3 - Превышено количество запросов на проверку фамилии
                        // 4 - Укажите свою фамилию
                        // 5 - Фамилия не указана, выполняем все действия по
                        // аналогии с кодом 1.
                        const isShowError = !data.data || !data.data.messageCode || data.data.messageCode === 0 || data.data.messageCode === 4;
    
                        if (isShowError) {
                            this.returnError({
                                code: 1
                            });
                            this.launchLoader(false);
                            return;
                        }
    
                        switch (data.data.messageCode) {
                            case 1:
                            case 5:
                                this.launchLoader(false);
                                this.goToNext(4);
                                break;
                            case 2:
                                this.isSurnameError = true;
                                this.surnameErrorText = 'Фамилия неверная или не&nbsp;найдена';
                                this.launchLoader(false);
                                break;
                            case 3: {
                                const time = data.data.blockTerm || 20;
                                const minutesBlock = time / 60;
                                const message = errorTimeMessage(time, 'проверки фамилии');
    
                                this.returnError({
                                    code: 2,
                                    data: {
                                        message,
                                        time: minutesBlock
                                    }
                                });
                                this.launchLoader(false);
                                break;
                            }
                            default:
                                this.returnError({
                                    code: 1
                                });
                                this.launchLoader(false);
                                return;
                        }
                    })
                    .catch((error) => {
                        this.launchLoader(false);
                        this.returnError({
                            code: 5,
                            data: error.response
                        });
                        return;
                    });
            } catch (error) {
                this.launchLoader(false);
                this.returnError({
                    code: 5,
                    data: error.response
                });
            }
        },

        /**
         * Переход к предыдущему шагу
         * @param step {Number} - шаг к которому нужно перейти в общей странице авторизации
         */
        goToBack(step) {
            this.$emit('goToBack', step);
        },

        /**
         * Переход к следующему шагу
         * @param step {Number} - шаг к которому нужно перейти в общей странице авторизации
         */
        goToNext(step) {
            this.$emit('goToNext', step);
        },

        /**
         * Возвращает ошибку в общую форму
         * @param code {Number} - код ошибки. 1 - ошибка обработки запроса в CRM. 2 - Блокировка на время. 3 - Ошибка со счётчиком попыток. 4 - Блокировка при привышении кол-ва попыток. 5 - Ошибка на сервере
         * @param data {Object} - данные для показа ошибки
         */
        returnError({code, data = null}) {
            this.$emit('showError', {code, data});
        },

        /**
         * Запуск/остановка лоадера
         * @param status {Boolean} - Вкл/выкл лоадер
         * @param message {String} - текст лоадера
         */
        launchLoader(status, message = '') {
            this.loading = status;
            this.loaderMessage = message;
        },

        /**
         * Успешное завершение лоадера
         */
        completeLoader() {
            this.hasLoaded = true;
            this.launchLoader(false, this.loaderMessage);
        },

        /**
         * Сбрасывает ошибку при вводе фамилии
         */
        clearInputsError() {
            this.isSurnameError = false;
        }
    }
};
</script>
